import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Trash2, ChevronDown, ChevronUp, Edit3, Download } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const ONDERDEEL_PATH = { groepspringen: "groepspringen", micro_meso: "micro-meso", individueel: "individueel" };
const TEMPLATE_BY = {
  groepspringen: { mt: "groepspringen_6_sprongen", pegasus: "groepspringen_6_sprongen", airtrack: "groepspringen_airtrack" },
  micro_meso: { mt: "micro_mt", pegasus: "micro_mt", airtrack: "micro_airtrack" },
  individueel: { mt: "individueel_mt", pegasus: "individueel_mt", airtrack: "individueel_airtrack" },
};

export default function MyRoutines() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [msg, setMsg] = useState("");
  const nav = useNavigate();

  const load = () => api.get("/routines").then(r => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Verwijderen?")) return;
    await api.delete(`/routines/${id}`); load();
  };

  const openInChecker = (r) => {
    const path = `/${ONDERDEEL_PATH[r.onderdeel] || r.onderdeel}/checker`;
    nav(path, { state: { prefill: r } });
  };

  const downloadExcel = async (r) => {
    try {
      const tpl = TEMPLATE_BY[r.onderdeel]?.[r.toestel] || "groepspringen_6_sprongen";
      const resp = await api.post("/excel/generate", {
        template: tpl,
        vereniging: "",
        team: r.titel,
        niveau: r.niveau,
        leeftijd: r.leeftijd,
        sprongen: r.sprongen.map(s => ({ naam: s.naam, d: s.d })),
      }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${r.titel.replace(/[^\w]+/g, "_")}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setMsg(formatError(e)); setTimeout(() => setMsg(""), 3000); }
  };

  return (
    <div data-testid="my-routines-page">
      <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">Mijn account</div>
      <h1 className="heading-display text-4xl mt-1 mb-6">Mijn opgeslagen sprongen</h1>
      {msg && <div className="mb-4 p-2 bg-red-50 border border-red-200 text-red-700 rounded text-sm">{msg}</div>}
      {items.length === 0 ? (
        <div className="gs-card p-6 text-slate-600">Geen opgeslagen sprongen. Ga naar de <Link to="/groepspringen/checker" className="text-orange-600 underline">Sprong Checker</Link> en sla er een op.</div>
      ) : (
        <ul className="space-y-3" data-testid="routines-list">
          {items.map(it => {
            const isOpen = openId === it.id;
            const totalD = it.sprongen.reduce((s, x) => s + (x.d || 0), 0);
            return (
              <li key={it.id} className="gs-card overflow-hidden" data-testid={`routine-${it.id}`}>
                <div className="p-4 flex items-center justify-between gap-3">
                  <button onClick={() => setOpenId(isOpen ? null : it.id)} className="flex-1 text-left flex items-center gap-2" data-testid={`toggle-${it.id}`}>
                    {isOpen ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                    <div>
                      <div className="font-bold">{it.titel}</div>
                      <div className="text-sm text-slate-600">{it.sprongen.length} sprongen · niveau {it.niveau} · {it.toestel} · totaal D {totalD.toFixed(1)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{new Date(it.created_at).toLocaleString()}</div>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => openInChecker(it)} className="text-sm bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-md font-semibold inline-flex items-center gap-1" data-testid={`open-${it.id}`}>
                      <Edit3 size={14}/> Openen
                    </button>
                    <button onClick={() => downloadExcel(it)} className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md font-semibold inline-flex items-center gap-1" data-testid={`excel-${it.id}`}>
                      <Download size={14}/> Excel
                    </button>
                    <button onClick={() => del(it.id)} className="text-slate-400 hover:text-red-600 p-1.5" data-testid={`del-${it.id}`}><Trash2 size={16}/></button>
                  </div>
                </div>
                {isOpen && (
                  <div className="px-4 pb-4 border-t bg-slate-50">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mt-3 mb-2">Sprongen:</div>
                    <ul className="space-y-1">
                      {it.sprongen.map((s, i) => (
                        <li key={i} className="flex items-center justify-between text-sm py-1">
                          <span>{s.naam}</span>
                          <span className="font-mono text-slate-600">{s.d?.toFixed?.(1) ?? s.d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
